import { cn } from "@facade/lib/utils";
import { cva } from "class-variance-authority";
import { HTMLAttributes, JSX, ReactNode } from "react";

export type TypographyVariants =
  | "hero-title"
  | "page-title"
  | "title"
  | "sub-title"
  | "text"
  | "text-lg"
  | "text-md"
  | "text-sm"
  | "text-xs"
  | "text-muted"
  | "blockquote"
  | "link";

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
  ellipsis?: boolean;
  variant?: TypographyVariants;
  children: ReactNode;
  className?: string;
}

const variantElements = {
  "hero-title": "h1",
  "page-title": "h2",
  title: "h3",
  "sub-title": "h4",
  text: "p",
  "text-lg": "div",
  "text-md": "div",
  "text-sm": "small",
  "text-xs": "small",
  "text-muted": "p",
  blockquote: "blockquote",
  link: "a",
} as const satisfies Record<TypographyVariants, keyof JSX.IntrinsicElements>;

const typographyVariants = cva("", {
  variants: {
    variant: {
      "hero-title":
        "scroll-m-20 text-center text-4xl font-extrabold tracking-tight text-balance",
      "page-title":
        "scroll-m-20 pb-2 text-3xl font-semibold tracking-tight first:mt-0",
      title: "scroll-m-20 text-2xl font-semibold tracking-tight",
      "sub-title": "scroll-m-20 text-xl font-semibold tracking-tight",
      text: "leading-7 [&:not(:first-child)]:mt-6",
      "text-lg": "text-lg font-semibold",
      "text-md": "text-[1rem]/none",
      "text-sm": "text-sm text-muted-foreground",
      "text-xs": "text-xs text-muted-foreground",
      "text-muted": "text-sm text-muted-foreground",
      blockquote: "mt-6 border-l-2 pl-6 italic",
      link: "text-primary underline-offset-4 hover:underline",
    },
  },
  defaultVariants: {
    variant: "text",
  },
});

export const Typography = ({
  variant = "text",
  children,
  className,
  ellipsis,
  ...rest
}: TypographyProps) => {
  const Component = variantElements[variant];
  return (
    <Component
      {...rest}
      className={cn(
        ellipsis && "overflow-hidden text-ellipsis whitespace-nowrap",
        typographyVariants({ variant }),
        className,
      )}
    >
      {children}
    </Component>
  );
};
